import { useMemo } from 'react';
import { UsersIcon } from '@heroicons/react/24/outline';
import {
    DataTable,
    expandAction,
    type DataTableExpansion,
    type DataTableFilter,
    type DataTableMode,
    type DataTablePagination,
    type DataTableRefs,
    type DataTableSearchConfig,
    type DataTableSort
} from '@odo/shared-ui';
import { PATIENTS_TD_PADDING, buildPatientColumns, type PatientsSortBy } from './patientColumns';
import type { PracticePatientRow } from './patientStatusJoin';

const SKELETON_ROW_COUNT = 8;

function SkeletonCell({ width, circle }: { width: string; circle?: boolean }) {
    return (
        <div
            className={`bg-gray-200 animate-pulse ${circle ? 'w-8 h-8 rounded-full' : `h-3.5 rounded ${width}`}`}
        />
    );
}

// Mirrors the real Patient cell's avatar-then-text layout so the skeleton
// doesn't shift width once real rows land.
function PatientSkeletonCell() {
    return (
        <div className="flex items-center gap-3">
            <SkeletonCell width="" circle />
            <SkeletonCell width="w-32" />
        </div>
    );
}

function SkeletonRows() {
    const widths = ['w-16', 'w-16', 'w-24', 'w-24', 'w-20'];
    return (
        <>
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                <tr key={i} className="h-14 border-b border-gray-200">
                    <td className={`${PATIENTS_TD_PADDING} align-middle`}>
                        <PatientSkeletonCell />
                    </td>
                    {widths.map((width, j) => (
                        <td key={j} className={`${PATIENTS_TD_PADDING} align-middle`}>
                            <SkeletonCell width={width} />
                        </td>
                    ))}
                </tr>
            ))}
        </>
    );
}

// Three distinct empty states: a practice can genuinely have zero patients
// (common - not an error), or a search and/or the Phase/Internal step pills
// can narrow the practice's real patients down to no matches - the latter
// reads as "no match" (mirrors PracticesTable's EmptyState) rather than "no
// patients", and its one clear action resets search and filters together.
function NoPatientsEmptyState({
    hasSearch,
    hasFilters,
    search,
    onClear
}: {
    hasSearch: boolean;
    hasFilters: boolean;
    search: string;
    onClear: () => void;
}) {
    if (!hasSearch && !hasFilters) {
        return (
            <div className="flex flex-col items-center gap-3 text-center">
                <UsersIcon className="h-12 w-12 text-gray-300" strokeWidth={1} />
                <p className="text-sm text-gray-500">This practice has no patients yet.</p>
            </div>
        );
    }
    return (
        <div className="flex flex-col items-center gap-3 text-center">
            <UsersIcon className="h-12 w-12 text-gray-300" strokeWidth={1} />
            <p className="text-sm text-gray-500">
                {hasSearch ? (
                    <>No patients match &ldquo;{search}&rdquo;.</>
                ) : (
                    'No patients match your filters.'
                )}
            </p>
            <button
                type="button"
                onClick={onClear}
                className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm mt-1"
            >
                {hasFilters ? 'Clear filters' : 'Clear search'}
            </button>
        </div>
    );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
    return (
        <div className="flex flex-col items-center gap-3 text-center">
            <p className="text-sm text-red-500 font-medium">Failed to load patients</p>
            <button
                type="button"
                onClick={onRetry}
                className="text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-1.5 transition-colors"
            >
                Retry
            </button>
        </div>
    );
}

interface PatientsTableProps {
    patients: PracticePatientRow[];
    isLoading: boolean;
    isError: boolean;
    search: string;
    searchConfig: DataTableSearchConfig;
    hasFilters: boolean;
    onClearFilters: () => void;
    filters: DataTableFilter[];
    onRetry: () => void;
    onOpenPatient: (patient: PracticePatientRow) => void;
    sort: DataTableSort<PatientsSortBy>;
    mode: DataTableMode;
    onModeChange: (mode: DataTableMode) => void;
    pagination: DataTablePagination;
    onLoadMore: () => void;
    hasMore: boolean;
    isLoadingMore: boolean;
    refs?: DataTableRefs;
    expansion?: DataTableExpansion;
}

export function PatientsTable({
    patients,
    isLoading,
    isError,
    search,
    searchConfig,
    hasFilters,
    onClearFilters,
    filters,
    onRetry,
    onOpenPatient,
    sort,
    mode,
    onModeChange,
    pagination,
    onLoadMore,
    hasMore,
    isLoadingMore,
    refs,
    expansion
}: PatientsTableProps) {
    // An error always takes over the body, even over stale cached rows -
    // matches the table's pre-migration behaviour of checking isError before
    // the empty state.
    const rows = isError ? [] : patients;

    const columns = useMemo(() => buildPatientColumns(), []);

    const onClear = () => {
        searchConfig.onChange('');
        onClearFilters();
    };

    return (
        <DataTable
            label="Patients"
            columns={columns}
            rows={rows}
            getRowKey={(patient) => patient.id}
            rowAction={{ onActivate: onOpenPatient }}
            tableLayout="auto"
            rowHeightClassName="h-14"
            sort={sort}
            mode={mode}
            onModeChange={onModeChange}
            pagination={pagination}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            isLoading={isLoading}
            loadingRows={<SkeletonRows />}
            emptyState={
                isError ? (
                    <ErrorState onRetry={onRetry} />
                ) : (
                    <NoPatientsEmptyState
                        hasSearch={search.length > 0}
                        hasFilters={hasFilters}
                        search={search}
                        onClear={onClear}
                    />
                )
            }
            emptyStateCellClassName="px-3 py-16"
            search={searchConfig}
            filters={filters}
            actions={expansion ? [expandAction(expansion, { label: 'Patients' })] : undefined}
            refs={refs}
            className="pb-8 overflow-hidden"
            expansion={expansion}
        />
    );
}
